import type { DefLookup } from "./catalog";
import { buildGraph, computeConnectedSolarInputs, v } from "./diagnostics/graph";
import type { Cable, System, SystemNode } from "./types";
import type { LoadCircuit } from "./load-circuits";

export type PvVoltageDropCircuit = LoadCircuit & { nominalV: number };

export type PvCurrentAnalysis = {
  /** Complete, matched array nameplate current; never limited by protection ratings. */
  byCable: Map<string, number>;
  unresolvedComponents: Array<{ nodeId: string; reason: "unsupported-pv-array" }>;
  unresolvedCableIds: Set<string>;
  /** One complete copper loop per matched series string, including shared
   * trunks at their aggregate current. Panel voltage transitions are not wire. */
  circuits: PvVoltageDropCircuit[];
};

const cache = new WeakMap<Record<string, SystemNode>, WeakMap<Cable[], WeakMap<DefLookup, PvCurrentAnalysis>>>();

/** Static PV sizing uses complete series strings and voltage-matched parallel
 * strings. Series current is the lowest declared Imp; parallel string currents
 * add. Manufacturer explanation:
 * https://www.victronenergy.com/blog/2015/11/10/connecting-different-sized-victron-energy-pv-modules-series-or-parallel/
 *
 * This is a nameplate design case, not an irradiance/MPPT simulation. Unknown
 * panel ratings, unequal parallel voltages, shared controllers and parallel
 * copper routes remain unresolved. Fuse and terminal ratings are constraints
 * to check against the result, never sources of current or current limiters. */
export function computePvCurrentAnalysis(
  nodesById: Record<string, SystemNode>, cables: Cable[], lookup: DefLookup,
): PvCurrentAnalysis {
  let byCables = cache.get(nodesById);
  if (!byCables) { byCables = new WeakMap(); cache.set(nodesById, byCables); }
  let byLookup = byCables.get(cables);
  if (!byLookup) { byLookup = new WeakMap(); byCables.set(cables, byLookup); }
  const cached = byLookup.get(lookup);
  if (cached) return cached;
  const system: System = { name: "PV design", version: 1, zoneDistances: {}, nodes: Object.values(nodesById), cables };
  const result: PvCurrentAnalysis = { byCable: new Map(), unresolvedComponents: [], unresolvedCableIds: new Set(), circuits: [] };
  byLookup.set(lookup, result);
  if (!system.nodes.some(node => node.kind === "component" && lookup(node.definitionId, node.customDefinition)?.category === "solar")) return result;
  const graph = buildGraph(system, lookup, { excludeSense: true });
  const parent = new Map<string, string>();
  const root = (x: string): string => {
    const p = parent.get(x);
    if (p == null) { parent.set(x, x); return x; }
    if (p === x) return x;
    const r = root(p); parent.set(x, r); return r;
  };
  const union = (a: string, b: string) => { const ra = root(a), rb = root(b); if (ra !== rb) parent.set(ra, rb); };
  // Only intra-component copper collapses here: every physical cable keeps
  // its own current, including two cables daisy-chained on a panel terminal.
  for (const [a, edges] of graph.adj) for (const edge of edges) {
    root(a); root(edge.vertex);
    if (edge.via.kind === "intra") union(a, edge.vertex);
  }
  type Wire = { id: string; a: string; b: string };
  const wires: Wire[] = [];
  const adj = new Map<string, Wire[]>();
  for (const cable of cables) {
    const a = v(cable.fromNodeId, cable.fromTerminalId), b = v(cable.toNodeId, cable.toTerminalId);
    if (!graph.adj.get(a)?.some(e => e.via.kind === "cable" && e.via.cableId === cable.id)) continue;
    const edge = { id: cable.id, a: root(a), b: root(b) }; wires.push(edge);
    (adj.get(edge.a) ?? adj.set(edge.a, []).get(edge.a)!).push(edge);
    (adj.get(edge.b) ?? adj.set(edge.b, []).get(edge.b)!).push(edge);
  }
  // Copper regions are equipotential for identifying a series string. This
  // does not discard the individual wire graph used to conserve current.
  const region = new Map<string, number>();
  const regions: string[][] = [];
  for (const vertex of parent.keys()) {
    const start = root(vertex);
    if (region.has(start)) continue;
    const queue = [start], id = regions.length; region.set(start, id);
    for (let i = 0; i < queue.length; i++) for (const edge of adj.get(queue[i]) ?? []) {
      const next = edge.a === queue[i] ? edge.b : edge.a;
      if (!region.has(next)) { region.set(next, id); queue.push(next); }
    }
    regions.push(queue);
  }
  const regionOf = (vertex: string) => region.get(root(vertex));
  const paths = computeConnectedSolarInputs(system, lookup);
  const memberships = new Map<string, number>();
  for (const path of paths) for (const id of path.panelNodeIds) memberships.set(id, (memberships.get(id) ?? 0) + 1);
  const resolvedPanels = new Set<string>();
  const unresolved = new Set<string>();
  const unresolvedPath = (ids: Iterable<string>, affected: Iterable<string>) => {
    for (const id of ids) unresolved.add(id);
    for (const id of affected) result.unresolvedCableIds.add(id);
  };
  for (const path of paths) {
    const pathRegions = new Set([...path.terminals].map(regionOf));
    const affected = wires.filter(edge => pathRegions.has(region.get(edge.a))).map(edge => edge.id);
    const fail = () => unresolvedPath([path.controllerNodeId, ...path.panelNodeIds], affected);
    if (path.positiveTerminals.size !== 1 || path.negativeTerminals.size !== 1) { fail(); continue; }
    const positive = [...path.positiveTerminals][0], negative = [...path.negativeTerminals][0];
    const top = regionOf(positive), bottom = regionOf(negative);
    if (top == null || bottom == null || top === bottom) { fail(); continue; }
    type Panel = { id: string; positive: string; negative: string; a: number; b: number; amps: number; volts: number };
    const panels: Panel[] = [];
    for (const id of path.panelNodeIds) {
      const node = nodesById[id]; if (node?.kind !== "component") continue;
      const def = lookup(node.definitionId, node.customDefinition); if (!def) continue;
      const pos = def.terminals.filter(t => t.polarity === "positive"), neg = def.terminals.filter(t => t.polarity === "negative");
      if (pos.length !== 1 || neg.length !== 1 || memberships.get(id) !== 1 ||
          !Number.isFinite(def.ampOutput) || !(def.ampOutput! > 0) || !Number.isFinite(def.voltage) || !(def.voltage! > 0)) continue;
      const p = v(id, pos[0].id), n = v(id, neg[0].id), a = regionOf(p), b = regionOf(n);
      if (a != null && b != null && a !== b) panels.push({ id, positive: p, negative: n, a, b, amps: def.ampOutput!, volts: def.voltage! });
    }
    if (panels.length !== path.panelNodeIds.size) { fail(); continue; }
    // Unrelated active terminals on a PV voltage level create an unknown
    // source interaction. A common negative reference alone is permitted.
    const pvLevels = new Set(panels.flatMap(panel => [panel.a, panel.b]));
    const coupledSource = system.nodes.some(node => {
      if (node.kind !== "component") return false;
      const def = lookup(node.definitionId, node.customDefinition);
      if (!def || !["battery", "source", "engine", "load", "charger"].includes(def.category)) return false;
      return def.terminals.some(term => {
        if (!["positive", "negative", "ground"].includes(term.polarity)) return false;
        const vertex = v(node.id, term.id), level = regionOf(vertex);
        return level != null && level !== bottom && pvLevels.has(level) &&
          !path.positiveTerminals.has(vertex) && !path.negativeTerminals.has(vertex);
      });
    });
    if (coupledSource) { fail(); continue; }
    const outgoing = new Map<number, Panel[]>(), incoming = new Map<number, Panel[]>();
    for (const panel of panels) {
      (outgoing.get(panel.a) ?? outgoing.set(panel.a, []).get(panel.a)!).push(panel);
      (incoming.get(panel.b) ?? incoming.set(panel.b, []).get(panel.b)!).push(panel);
    }
    const strings: Panel[][] = [], used = new Set<string>();
    let valid = !incoming.has(top) && !outgoing.has(bottom);
    for (const head of outgoing.get(top) ?? []) {
      const string: Panel[] = []; let panel: Panel | undefined = head;
      while (panel) {
        if (used.has(panel.id)) { valid = false; break; }
        used.add(panel.id); string.push(panel);
        if (panel.b === bottom) break;
        const next: Panel[] = outgoing.get(panel.b) ?? [];
        if (next.length !== 1 || incoming.get(panel.b)?.length !== 1) { valid = false; break; }
        panel = next[0];
      }
      strings.push(string);
    }
    if (!valid || used.size !== panels.length || !strings.length) { fail(); continue; }
    const voltages = strings.map(string => string.reduce((sum, panel) => sum + panel.volts, 0));
    if (voltages.some(volts => Math.abs(volts - voltages[0]) > 1e-6)) { fail(); continue; }
    const injections = new Map<string, number>();
    const add = (vertex: string, amps: number) => { const key = root(vertex); injections.set(key, (injections.get(key) ?? 0) + amps); };
    let total = 0;
    for (const string of strings) {
      const amps = Math.min(...string.map(panel => panel.amps)); total += amps;
      for (const panel of string) { add(panel.positive, amps); add(panel.negative, -amps); }
    }
    add(positive, -total); add(negative, total);
    const currents = new Map<string, number>();
    // On a tree, a conductor carries the sum of injections on either side.
    // Reject loops instead of assuming equal resistance or picking one path.
    for (const id of new Set([...injections.keys()].map(key => region.get(key)!))) {
      const vertices = regions[id], edges = wires.filter(edge => region.get(edge.a) === id);
      if (edges.length !== vertices.length - 1) { valid = false; break; }
      const seen = new Set<string>();
      const visit = (vertex: string, previous?: string): number => {
        seen.add(vertex); let amps = injections.get(vertex) ?? 0;
        for (const edge of adj.get(vertex) ?? []) {
          if (edge.id === previous) continue;
          const next = edge.a === vertex ? edge.b : edge.a;
          if (seen.has(next)) { valid = false; continue; }
          const branch = visit(next, edge.id); amps += branch;
          currents.set(edge.id, Math.abs(branch));
        }
        return amps;
      };
      if (Math.abs(visit(vertices[0])) > 1e-7) valid = false;
    }
    // A cable shared with another array would require signed joint source
    // alternatives. Keep it unresolved instead of overwriting either bound.
    if ([...currents].some(([id, amps]) => amps > 1e-9 && result.byCable.has(id))) valid = false;
    if (!valid) { fail(); continue; }
    // Each copper region is a validated tree. Follow its unique path between
    // each source/consumer pair; never add a sibling parallel string's loss.
    const copperPath = (from: string, to: string): string[] => {
      const start = root(from), end = root(to);
      const pending = [{ vertex: start, cables: [] as string[] }];
      const seen = new Set([start]);
      for (const item of pending) {
        if (item.vertex === end) return item.cables;
        for (const edge of adj.get(item.vertex) ?? []) {
          const next = edge.a === item.vertex ? edge.b : edge.a;
          if (seen.has(next)) continue;
          seen.add(next);
          pending.push({ vertex: next, cables: [...item.cables, edge.id] });
        }
      }
      return [];
    };
    for (const [i, string] of strings.entries()) {
      const cableIds = copperPath(positive, string[0].positive);
      for (let j = 1; j < string.length; j++) {
        cableIds.push(...copperPath(string[j - 1].negative, string[j].positive));
      }
      cableIds.push(...copperPath(string.at(-1)!.negative, negative));
      result.circuits.push({ loadVertex: positive, pos: [...new Set(cableIds)], neg: [],
        complete: true, nominalV: voltages[i] });
    }
    for (const [id, amps] of currents) if (amps > 1e-9) result.byCable.set(id, amps);
    for (const id of path.panelNodeIds) resolvedPanels.add(id);
  }
  // A disconnected or undeclared panel has no verified design current. Mark
  // its local conductors as unknown so a fuse cannot masquerade as Imp.
  for (const node of system.nodes) {
    if (node.kind !== "component" || resolvedPanels.has(node.id)) continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (def?.category !== "solar") continue;
    const connected = cables.filter(c => c.fromNodeId === node.id || c.toNodeId === node.id);
    if (connected.length) unresolvedPath([node.id], connected.map(c => c.id));
  }
  for (const id of result.unresolvedCableIds) result.byCable.delete(id);
  result.circuits = result.circuits.filter(c => c.pos.every(id => !result.unresolvedCableIds.has(id)));
  result.unresolvedComponents = [...unresolved].map(nodeId => ({ nodeId, reason: "unsupported-pv-array" }));
  return result;
}
